/**
 * AdminInventoryPage: edit the object catalog's defaults (seats,
 * dimensions) and venue stock limits (PRD's locked access model — Admin
 * only, "to prevent accidental changes that could affect every future
 * layout"). RLS is the real boundary; this page existing only for Admin
 * routes is the matching UI-layer signal.
 */

import { useState } from 'react';
import {
  useObjectCatalog,
  useInventoryLimits,
  useChairStock,
  useUpdateObjectCatalogEntry,
  useUpdateInventoryLimit,
  useUpdateChairStock,
} from '../api/inventoryLimits';
import type { ObjectCatalogEntry } from '../domain/types';
import { Toast, ToastStack } from '../components/shared/Toast';

export function AdminInventoryPage() {
  const { data: catalog, isLoading: catalogLoading } = useObjectCatalog();
  const { data: limits } = useInventoryLimits();
  const { data: chairStock } = useChairStock();
  const updateCatalogEntry = useUpdateObjectCatalogEntry();
  const updateLimit = useUpdateInventoryLimit();
  const updateChairStock = useUpdateChairStock();

  const [editingChairStock, setEditingChairStock] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);

  if (catalogLoading || !catalog) {
    return <p className="p-8 font-body text-sm text-ink-400">{'Loading\u2026'}</p>;
  }

  const stockLimited = catalog.filter((c) => c.hasStockLimit);

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <h1 className="font-display text-2xl text-ink-700">Inventory</h1>
      <p className="mt-1 font-body text-sm text-ink-400">
        Editing these values affects every future generated layout venue-wide. Changes here do
        not retroactively alter already-saved layouts.
      </p>

      <section className="mt-6 rounded-lg border border-ivory-300 bg-white p-5">
        <h2 className="font-display text-lg text-ink-700">Chairs</h2>
        <div className="mt-3 flex items-center gap-3">
          <input
            type="number"
            min={0}
            value={editingChairStock ?? chairStock ?? 0}
            onChange={(e) => setEditingChairStock(Number(e.target.value))}
            className="w-28 rounded border border-ivory-300 px-3 py-2 font-body text-ink-700"
          />
          <button
            onClick={() => {
              if (editingChairStock !== null) {
                updateChairStock.mutate(editingChairStock, {
                  onSuccess: () => {
                    setEditingChairStock(null);
                    setToast({ message: 'Chair stock updated.', tone: 'success' });
                  },
                  onError: () => setToast({ message: 'Could not save \u2014 please try again.', tone: 'error' }),
                });
              }
            }}
            disabled={editingChairStock === null || updateChairStock.isPending}
            className="rounded bg-evergreen-600 px-4 py-2 font-body text-sm text-ivory-50 hover:bg-evergreen-700 disabled:opacity-40"
          >
            Save
          </button>
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-ivory-300 bg-white p-5">
        <h2 className="font-display text-lg text-ink-700">Stock-limited items</h2>
        <table className="mt-3 w-full text-left">
          <thead>
            <tr className="font-body text-xs uppercase tracking-wide text-ink-400">
              <th className="py-2">Item</th>
              <th className="py-2">Venue stock</th>
            </tr>
          </thead>
          <tbody>
            {stockLimited.map((entry) => {
              const limit = limits?.find((l) => l.itemType === entry.itemType);
              return (
                <StockRow
                  key={entry.itemType}
                  entry={entry}
                  currentMax={limit?.maxQuantity ?? 0}
                  onSave={(value) =>
                    updateLimit.mutate(
                      { itemType: entry.itemType, maxQuantity: value },
                      {
                        onSuccess: () => setToast({ message: `${entry.displayName} stock updated.`, tone: 'success' }),
                        onError: () => setToast({ message: 'Could not save \u2014 please try again.', tone: 'error' }),
                      }
                    )
                  }
                />
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="mt-6 rounded-lg border border-ivory-300 bg-white p-5">
        <h2 className="font-display text-lg text-ink-700">Default seats &amp; sizes</h2>
        <table className="mt-3 w-full text-left">
          <thead>
            <tr className="font-body text-xs uppercase tracking-wide text-ink-400">
              <th className="py-2">Item</th>
              <th className="py-2">Default seats</th>
              <th className="py-2">Max seats</th>
            </tr>
          </thead>
          <tbody>
            {catalog
              .filter((c) => c.defaultSeats !== null || c.maxSeats !== null)
              .map((entry) => (
                <SeatsRow
                  key={entry.itemType}
                  entry={entry}
                  onSave={(changes) =>
                    updateCatalogEntry.mutate(
                      { itemType: entry.itemType, ...changes },
                      {
                        onSuccess: () => setToast({ message: `${entry.displayName} defaults updated.`, tone: 'success' }),
                        onError: () => setToast({ message: 'Could not save \u2014 please try again.', tone: 'error' }),
                      }
                    )
                  }
                />
              ))}
          </tbody>
        </table>
      </section>

      <ToastStack>
        {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
      </ToastStack>
    </div>
  );
}

function StockRow({
  entry,
  currentMax,
  onSave,
}: {
  key?: React.Key;
  entry: ObjectCatalogEntry;
  currentMax: number;
  onSave: (value: number) => void;
}) {
  const [value, setValue] = useState(currentMax);
  const dirty = value !== currentMax;

  return (
    <tr className="border-t border-ivory-100">
      <td className="py-2 font-body text-sm text-ink-600">{entry.displayName}</td>
      <td className="py-2">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            value={value}
            onChange={(e) => setValue(Number(e.target.value))}
            className="w-20 rounded border border-ivory-300 px-2 py-1 font-body text-sm text-ink-700"
          />
          {dirty && (
            <button
              onClick={() => onSave(value)}
              className="font-body text-xs text-evergreen-600 underline"
            >
              Save
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function SeatsRow({
  entry,
  onSave,
}: {
  key?: React.Key;
  entry: ObjectCatalogEntry;
  onSave: (changes: { defaultSeats?: number; maxSeats?: number }) => void;
}) {
  const [defaultSeats, setDefaultSeats] = useState(entry.defaultSeats ?? 0);
  const [maxSeats, setMaxSeats] = useState(entry.maxSeats ?? 0);
  const dirty = defaultSeats !== entry.defaultSeats || maxSeats !== entry.maxSeats;

  return (
    <tr className="border-t border-ivory-100">
      <td className="py-2 font-body text-sm text-ink-600">{entry.displayName}</td>
      <td className="py-2">
        <input
          type="number"
          min={0}
          value={defaultSeats}
          onChange={(e) => setDefaultSeats(Number(e.target.value))}
          className="w-16 rounded border border-ivory-300 px-2 py-1 font-body text-sm text-ink-700"
        />
      </td>
      <td className="py-2">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            value={maxSeats}
            onChange={(e) => setMaxSeats(Number(e.target.value))}
            className="w-16 rounded border border-ivory-300 px-2 py-1 font-body text-sm text-ink-700"
          />
          {dirty && (
            <button
              onClick={() => onSave({ defaultSeats, maxSeats })}
              className="font-body text-xs text-evergreen-600 underline"
            >
              Save
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
